import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Check, Phone } from 'lucide-react';
import { useToast } from "@/hooks/use-toast";
import PaymentPrivacyDisclosure from '@/components/PaymentPrivacyDisclosure';
import { PaymentBadges } from '@/components/Pricing';
import UsaComplianceInformation from '@/components/UsaComplianceInformation';
import { useLocation } from 'react-router-dom';
import { checkoutRequest, minuteAddonId, smsAddonId } from '@/lib/checkoutContract';
import { standardPackTelemetrySku } from '@/lib/productTelemetrySku';
import { countriesForFamily, loadPublicEsimCatalogue, productIsAvailable } from '@/lib/publicEsimCatalogue';

// Updated Virtual Number countries to show only UK and USA
const numberCountries = ['United Kingdom', 'United States'];

const packData = {
  '1-week': [
    { id: '1w-1g', data: '1GB', duration: '7 Days', basePrice: 7.99, highlight: false, baseDesc: 'Quick trip data' },
    { id: '1w-5g', data: '5GB', duration: '7 Days', basePrice: 12.99, highlight: false, baseDesc: '5GB travel data' },
    { id: '1w-10g', data: '10GB', duration: '7 Days', basePrice: 18.99, highlight: true, baseDesc: '10GB travel data' },
    { id: '1w-50g', data: '50GB', duration: '7 Days', basePrice: 39.99, highlight: false, baseDesc: '50GB travel data' },
  ],
  '2-weeks': [
    { id: '2w-5g', data: '5GB', duration: '14 Days', basePrice: 23.99, highlight: false, baseDesc: '5GB travel data' },
    { id: '2w-10g', data: '10GB', duration: '14 Days', basePrice: 33.99, highlight: true, baseDesc: '10GB travel data' },
    { id: '2w-50g', data: '50GB', duration: '14 Days', basePrice: 69.99, highlight: false, baseDesc: '50GB travel data' },
  ],
  '1-month': [
    { id: '1m-10g', data: '10GB', duration: '30 Days', basePrice: 34.99, highlight: false, baseDesc: '10GB travel data' },
    { id: '1m-20g', data: '20GB', duration: '30 Days', basePrice: 59.99, highlight: true, baseDesc: '20GB travel data' },
    { id: '1m-50g', data: '50GB', duration: '30 Days', basePrice: 99.99, highlight: false, baseDesc: '50GB travel data' },
  ]
};

const numberPricing = {
  '1-week': { price: 2.00, included: '30 min + 20 SMS' },
  '2-weeks': { price: 3.50, included: '60 min + 40 SMS' },
  '1-month': { price: 6.00, included: '120 min + 60 SMS' },
};

const minuteOptions = [
  { label: 'None', price: 0 },
  { label: '+30 min £1.50', price: 1.50 },
  { label: '+1 hour £3.00', price: 3.00 },
];

const smsOptions = [
  { label: 'None', price: 0 },
  { label: '+50 SMS £1.50', price: 1.50 },
  { label: '+100 SMS £3.00', price: 3.00 },
];

const CustomSelect = ({ value, onChange, options, isOpen, setIsOpen, className, focusBorderColor, dropdownTextClass = "text-xs", placeholder = '' }) => {
  const selectRef = useRef(null);
  const activeColorClass = focusBorderColor ? focusBorderColor.replace('border-', 'text-') : 'text-white';

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [setIsOpen]);

  return (
    <div className="relative w-full text-left" ref={selectRef}>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen((prev) => !prev);
        }}
        className={`${className} flex items-center justify-between ${isOpen ? focusBorderColor : 'border-white/10'}`}
      >
        <span className="truncate">{options.find(o => (o.value !== undefined ? o.value : o) === value)?.label || value || placeholder}</span>
        <svg className={`w-4 h-4 ml-2 shrink-0 transition-colors ${isOpen ? activeColorClass : 'text-gray-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isOpen ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
        </svg>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
            className={`absolute z-[100] w-full mt-1 bg-[#0f172a] border ${focusBorderColor || 'border-white/20'} rounded-lg shadow-xl max-h-48 overflow-y-auto custom-scrollbar`}
          >
            {options.map((opt) => {
              const optValue = opt.value !== undefined ? opt.value : opt;
              const optLabel = opt.label !== undefined ? opt.label : opt;
              return (
                <div
                  key={optValue}
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(optValue);
                    setIsOpen(false);
                  }}
                  className={`px-3 py-2 ${dropdownTextClass} text-white hover:bg-white/10 cursor-pointer transition-colors ${value === optValue ? 'bg-white/5 font-medium' : ''}`}
                >
                  {optLabel}
                </div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const PackCard = ({ plan, activeTab, dataCountry, dataCountryCode, handleBuy, anchorId, selectionRequired }) => {
  const telemetrySku = selectionRequired ? '' : standardPackTelemetrySku('travel', plan.data, plan.duration, dataCountry, dataCountryCode);
  const [expanded, setExpanded] = useState(false);
  const [numberCountry, setNumberCountry] = useState('United Kingdom');
  const [extraMinutes, setExtraMinutes] = useState(0);
  const [extraSms, setExtraSms] = useState(0);
  const [usaConsent, setUsaConsent] = useState(false);
  const [userMobile, setUserMobile] = useState('');

  const [isNumberCountryDropdownOpen, setIsNumberCountryDropdownOpen] = useState(false);
  const [isMinutesDropdownOpen, setIsMinutesDropdownOpen] = useState(false);
  const [isSmsDropdownOpen, setIsSmsDropdownOpen] = useState(false);

  const onToggle = () => setExpanded(prev => !prev);

  const numData = numberPricing[activeTab];

  const calculateTotal = () => {
    let total = plan.basePrice;
    if (expanded) {
      total += numData.price;
      total += extraMinutes;
      total += extraSms;
    }
    return total.toFixed(2);
  };

  const getDynamicDesc = () => {
    const totalNum = parseFloat(calculateTotal());
    const gb = parseInt(plan.data);
    const perGb = (totalNum / gb).toFixed(2);

    if (plan.data === '50GB') {
      return `${plan.baseDesc}, lowest cost (~£${perGb}/GB)`;
    } else {
      return `${plan.baseDesc} at ~£${perGb}/GB`;
    }
  };

  return (
    <motion.div
      id={anchorId}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className={`scroll-mt-[150px] relative flex flex-col p-5 rounded-xl border transition-all duration-300 group hover:-translate-y-1 hover:shadow-2xl overflow-visible h-auto mb-8 md:mb-0 ${plan.highlight
        ? 'public-featured-product-card bg-[#0f172a] border-[#2dd4bf] shadow-[0_4px_30px_rgba(45,212,191,0.15)]'
        : 'bg-[#1e293b] border-white/5 hover:border-white/20 shadow-lg'
        }`}
    >
      <div className="flex-1 flex flex-col overflow-visible">
        <div className="shrink-0 mb-3">
          <h3 className={`mb-1 text-xl font-bold md:text-lg ${plan.highlight ? 'text-[#2dd4bf]' : 'text-white'}`}>
            {plan.data} Data Pack
          </h3>
          <p className="text-xs text-gray-400 mb-2">{selectionRequired ? 'Choose destination' : dataCountry} • {plan.duration}</p>
          <p className={`text-xs font-medium mb-3 pb-3 border-b border-white/5 ${plan.highlight ? 'text-[#2dd4bf]/80' : 'text-gray-400'}`}>
            {selectionRequired ? plan.baseDesc : getDynamicDesc()}
          </p>
          {selectionRequired ? (
            <p className="mb-1 text-sm font-bold text-white">Choose destination to see price</p>
          ) : (
            <div className="flex items-baseline gap-1 mb-1"><span className="text-2xl font-black text-white">£{calculateTotal()}</span></div>
          )}
        </div>

        <ul className="space-y-2 shrink-0 pt-1 mb-4">
          <li className="flex items-start gap-2 text-xs text-gray-300">
            <Check className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${plan.highlight ? 'text-[#2dd4bf]' : 'text-[#38bdf8]'}`} />
            <span>eSIM Included</span>
          </li>
          <li className="flex items-start gap-2 text-xs text-gray-300">
            <Check className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${plan.highlight ? 'text-[#2dd4bf]' : 'text-[#38bdf8]'}`} />
            <span>Travel Data ({plan.data})</span>
          </li>
          <li className="flex items-start gap-2 text-xs text-gray-300">
            <Check className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${plan.highlight ? 'text-[#2dd4bf]' : 'text-[#38bdf8]'}`} />
            <span>Optional UK/USA Number</span>
          </li>
        </ul>

        <div className="space-y-3 shrink-0 pb-1">
          <div className="p-3 rounded-xl bg-black/30 border border-white/5">
            <div className="flex items-center justify-between cursor-pointer" onClick={(e) => { e.preventDefault(); onToggle(); }}>
              <span className="text-xs font-semibold flex items-center gap-2 text-gray-200">
                <Phone className="w-3.5 h-3.5 text-[#38bdf8]" /> Add Virtual Number
              </span>
              <div className={`w-10 h-5 rounded-full p-0.5 transition-colors cursor-pointer ${expanded ? 'bg-[#2dd4bf]' : 'bg-gray-600'}`}>
                <div className={`w-4 h-4 bg-white rounded-full transition-transform duration-200 ${expanded ? 'translate-x-5' : 'translate-x-0'}`} />
              </div>
            </div>

            <AnimatePresence>
              {expanded && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="space-y-2.5 mt-3 overflow-visible">
                  <div>
                    <label className="text-[10px] font-medium text-gray-200 uppercase tracking-wider block mb-1">Choose Number Country</label>
                    <CustomSelect
                      value={numberCountry}
                      onChange={setNumberCountry}
                      options={numberCountries}
                      isOpen={isNumberCountryDropdownOpen}
                      setIsOpen={setIsNumberCountryDropdownOpen}
                      className="w-full bg-[#0f172a] border border-white/10 rounded-lg p-2 text-xs text-white transition-colors"
                      focusBorderColor="border-[#2dd4bf]"
                  />
                </div>

                  <div className="text-[10px] text-[#2dd4bf] bg-[#2dd4bf]/10 p-2 rounded-lg border border-[#2dd4bf]/20 font-medium flex flex-col items-center gap-1">
                    <div className="flex justify-between w-full">
                      <span>Included:</span>
                      <span>{numData.included} (+£{numData.price.toFixed(2)})</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-2.5">
                    <div>
                      <label className="text-[10px] font-medium text-gray-200 uppercase tracking-wider block mb-1">Add Extra Minutes</label>
                      <CustomSelect
                        value={extraMinutes}
                        onChange={setExtraMinutes}
                        options={minuteOptions.map(opt => ({ label: opt.label, value: opt.price }))}
                        isOpen={isMinutesDropdownOpen}
                        setIsOpen={setIsMinutesDropdownOpen}
                        className="w-full bg-[#0f172a] border border-white/10 rounded-lg p-2 text-xs text-white transition-colors"
                        focusBorderColor="border-[#2dd4bf]"
                    />
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-gray-200 uppercase tracking-wider block mb-1">Add Extra SMS</label>
                      <CustomSelect
                        value={extraSms}
                        onChange={setExtraSms}
                        options={smsOptions.map(opt => ({ label: opt.label, value: opt.price }))}
                        isOpen={isSmsDropdownOpen}
                        setIsOpen={setIsSmsDropdownOpen}
                        className="w-full bg-[#0f172a] border border-white/10 rounded-lg p-2 text-xs text-white transition-colors"
                        focusBorderColor="border-[#2dd4bf]"
                      />
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {expanded && numberCountry === 'United States' && (
          <div className="mt-3 shrink-0 space-y-3 rounded-xl border border-[#00ffc8]/30 bg-[#00ffc8]/[0.035] p-3 shadow-[0_0_20px_rgba(0,255,200,0.14)]">
            <UsaComplianceInformation />
            <div>
              <label className="text-[10px] font-medium text-amber-400 uppercase tracking-wider block mb-1.5">Your mobile number (required for USA)</label>
              <input type="tel" value={userMobile} onChange={e => setUserMobile(e.target.value)} placeholder="+1 (555) 000-0000"
                className="w-full bg-[#0f172a] border border-white/10 rounded-lg p-2.5 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 transition-all" />
            </div>
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input type="checkbox" checked={usaConsent} onChange={e => setUsaConsent(e.target.checked)} className="mt-0.5 w-4 h-4 accent-amber-500 shrink-0" />
              <span className="text-[10px] text-gray-300 leading-relaxed">
                I consent to receive transactional SMS from PhantomPathVPN regarding my service activation, access codes, and security alerts. Msg &amp; data rates may apply. Msg frequency varies. Reply STOP to opt-out, HELP for help. View <a href="/terms" className="text-[#38bdf8] underline">Terms</a> and <a href="/privacy-policy" className="text-[#38bdf8] underline">Privacy Policy</a>.
              </span>
            </label>
          </div>
        )}
      </div>

      <div className="mt-auto pt-4 shrink-0 w-full flex flex-col gap-3 border-t border-white/5">
        <Button
          data-ga-event="begin_checkout"
          data-funnel-stage="product_purchase_clicked"
          data-product-name={`${plan.data} ${plan.duration} Travel Pack`}
          data-product-id={telemetrySku}
          data-value={calculateTotal()}
          data-currency="GBP"
          disabled
          onClick={() => handleBuy(calculateTotal(), plan.data + " " + plan.duration + " Travel Pack", {
            esimCountry: dataCountry,
            includeVirtualNumber: expanded,
            ...(expanded ? {
              virtualNumberCountry: numberCountry,
              minuteAddonId: minuteAddonId(extraMinutes),
              smsAddonId: smsAddonId(extraSms),
            } : {}),
          }, telemetrySku)}
          className={`w-full font-bold rounded-lg h-10 text-xs uppercase tracking-wider transition-all duration-300 glow-phantom glow-phantom-hover mt-auto ${selectionRequired || (expanded && numberCountry === 'United States' && (!usaConsent || !userMobile.trim()))
            ? 'bg-white/5 text-gray-600 border border-white/10 cursor-not-allowed'
            : plan.highlight
              ? 'bg-[#2dd4bf] text-[#020617] hover:bg-white hover:text-black'
              : 'bg-[#38bdf8]/10 text-[#38bdf8] border border-[#38bdf8]/30 hover:bg-[#38bdf8] hover:text-white'
            }`}
        >
          {selectionRequired ? 'Choose destination' : 'Get Data Pack'}
        </Button>
        <PaymentBadges methods={['stripe', 'paypal']} />
      </div>
    </motion.div>
  );
};

const TravelPacks = () => {
  const { hash } = useLocation();
  const [activeTab, setActiveTab] = useState('1-week');
  const [globalDataCountry, setGlobalDataCountry] = useState('');
  const { toast } = useToast();
  const [isGlobalCountryDropdownOpen, setIsGlobalCountryDropdownOpen] = useState(false);
  const [esimCatalogue, setEsimCatalogue] = useState(null);

  useEffect(() => {
    let active = true;
    loadPublicEsimCatalogue().then((catalogue) => { if (active) setEsimCatalogue(catalogue); })
      .catch(() => { if (active) setEsimCatalogue(null); });
    return () => { active = false; };
  }, []);

  const dataCountries = countriesForFamily(esimCatalogue, 'travel');
  const selectedCountry = dataCountries.find((country) => country.value === globalDataCountry) || null;
  const visiblePlans = selectedCountry
    ? packData[activeTab].filter((plan) => productIsAvailable(esimCatalogue, 'travel', selectedCountry.code, plan.data, Number.parseInt(plan.duration, 10)))
    : packData[activeTab];

  useEffect(() => {
    const syncProductTarget = () => {
      const target = hash.slice(1);
      const match = target.match(/^travel-esim-(?:1gb|5gb|10gb|20gb|50gb)-(1-week|2-week|30-day)$/);
      if (!match) return;
      setActiveTab({ '1-week': '1-week', '2-week': '2-weeks', '30-day': '1-month' }[match[1]]);
      window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
        document.getElementById(target)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }));
    };
    syncProductTarget();
  }, [hash]);

  const [checkoutInfo, setCheckoutInfo] = useState(null);
  const handleBuy = (total, name, selection, productSku) => {
    setCheckoutInfo({ total, name, selection, productSku });
  };

  return (
    <section id="travel-packs" className="scroll-mt-[100px] py-10 lg:py-14 px-4 bg-[#050b14]">
      <span id="esim-data" className="block scroll-mt-[100px]" aria-hidden="true" />
      <div className="container mx-auto max-w-7xl">
        <div className="text-center mb-8">
          <h2 className="mx-auto mb-3 max-w-4xl text-xl font-extrabold leading-tight tracking-tight text-white sm:text-2xl md:text-3xl">Travel eSIM data — stay connected abroad.</h2>
          <p className="mb-5 text-base font-semibold text-[#2dd4bf]">No subscription. No commitment.</p>
          <div className="mx-auto mt-6 mb-8 flex max-w-3xl flex-col items-stretch justify-center gap-4 md:flex-row">
            <div className="flex min-w-0 flex-1 flex-col rounded-2xl border border-[#0b466d]/45 bg-[#0f172a] p-3 text-left shadow-[0_0_22px_rgba(11,70,109,0.12)]">
              <span className="mb-2 text-[11px] font-black uppercase tracking-[0.16em] text-[#0b466d]">1 · Choose travel country</span>
              <div className="flex items-center gap-2">
                <span className="shrink-0 text-xs font-semibold text-[#173c5e]">Travel Country:</span>
                <div className="min-w-0 flex-1">
                  <CustomSelect
                    value={globalDataCountry}
                    onChange={setGlobalDataCountry}
                    options={dataCountries}
                    isOpen={isGlobalCountryDropdownOpen}
                    setIsOpen={setIsGlobalCountryDropdownOpen}
                    className="w-full bg-[#050b14] border border-[#2dd4bf]/40 rounded-lg px-3 py-2 text-sm text-white font-bold transition-colors cursor-pointer"
                    focusBorderColor="border-[#0b7fd1]"
                    dropdownTextClass="text-sm"
                    placeholder="Choose your destination"
                  />
                </div>
              </div>
            </div>

            <div className="flex min-w-0 flex-1 flex-col rounded-2xl border border-white/10 bg-[#0f172a] p-3 text-left shadow-lg">
              <span className="mb-2 text-[11px] font-black uppercase tracking-[0.16em] text-gray-300">2 · Choose duration</span>
              <div className="grid grid-cols-3 rounded-xl border border-white/5 bg-[#1e293b] p-1">
              {[
                { id: '1-week', label: '1 Week' },
                { id: '2-weeks', label: '2 Weeks' },
                { id: '1-month', label: '1 Month' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-2 py-2 rounded-lg text-sm font-bold transition-all duration-300 ${activeTab === tab.id
                    ? 'bg-[#2dd4bf] text-[#020617] shadow-md'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`}
                >
                  {tab.label}
                </button>
              ))}
              </div>
            </div>
          </div>
          <div className="esim-compatibility-note">
            <a href="/guides/avoid-roaming-charges-with-esim">Check whether your phone supports eSIM</a>
            <p>Before buying, check that your phone supports eSIM and is network-unlocked.</p>
            <p>Purchase your plan, install the eSIM from your Portal details, and connect when you arrive.</p>
          </div>
        </div>

        <div className="flex flex-col md:grid md:grid-cols-2 lg:grid-cols-4 gap-0 md:gap-8 lg:gap-6 justify-center max-w-6xl mx-auto items-stretch">
          {visiblePlans.map((plan) => (
            <PackCard
              key={plan.id}
              plan={plan}
              activeTab={activeTab}
              dataCountry={selectedCountry?.value || ''}
              dataCountryCode={selectedCountry?.code || ''}
              selectionRequired={!selectedCountry}
              handleBuy={handleBuy}
              anchorId={`travel-esim-${plan.data.toLowerCase()}-${{ '1-week': '1-week', '2-weeks': '2-week', '1-month': '30-day' }[activeTab]}`}
            />
          ))}
        </div>

        <div className="mx-auto mt-6 max-w-4xl">
          <PaymentPrivacyDisclosure showGeneral={false}>
            <p>18+ ONLY. Travel eSIM service begins after successful payment. Cancellation and refund rights depend on the applicable law and service status; see the Refund Policy.</p>
            <p>Telecom operations may be monitored for fraud, abuse, infrastructure security and legal compliance. Virtual numbers are provided through third-party telecommunications providers. Compatibility with verification services depends on the policies of each platform. SMS delivery is not guaranteed for all services.</p>
            <p><strong className="text-red-400 font-medium">IMPORTANT:</strong> PhantomPath numbers DO NOT support emergency calling (999, 112, or 911). You must maintain an alternative means of contacting emergency services.</p>
          </PaymentPrivacyDisclosure>
        </div>
      </div>


      {checkoutInfo && (
        <div className="public-checkout-overlay" style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', padding: '1rem' }} onClick={() => setCheckoutInfo(null)}>
          <div className="public-checkout-dialog" data-ga-location="travel_pack_payment_dialog" data-product-id={checkoutInfo.productSku} style={{ background: '#111', border: '1px solid rgba(45,212,191,0.2)', borderRadius: '1rem', width: '100%', maxWidth: '24rem', overflow: 'hidden' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.25rem', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
              <div>
                <h3 style={{ color: 'white', fontWeight: 'bold', fontSize: '1rem' }}>{checkoutInfo.name}</h3>
                <p style={{ color: '#2dd4bf', fontSize: '1.5rem', fontWeight: 'bold', marginTop: '0.25rem' }}>{String.fromCharCode(163)}{checkoutInfo.total}</p>
              </div>
              <button onClick={() => setCheckoutInfo(null)} style={{ width: '2rem', height: '2rem', borderRadius: '50%', background: 'transparent', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '1.25rem' }}>{String.fromCharCode(10005)}</button>
            </div>
            <div data-checkout-consent-scope style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <p style={{ color: '#9ca3af', fontSize: '0.75rem', marginBottom: '0.5rem' }}>Choose your payment method:</p>
              <button data-ga-event="add_payment_info" data-product-name={checkoutInfo.name} data-value={checkoutInfo.total} data-currency="GBP" data-payment-method="stripe" onClick={() => { fetch('/api/checkout/dynamic', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(checkoutRequest(checkoutInfo, 'stripe')) }).then(r => r.json()).then(d => { if (d.url) window.location.href = d.url; }); }} style={{ width: '100%', height: '3rem', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', fontWeight: 'bold', fontSize: '0.875rem', borderRadius: '0.75rem', cursor: 'pointer' }}>
                Pay with Card (Stripe)
              </button>
              <button data-ga-event="add_payment_info" data-product-name={checkoutInfo.name} data-value={checkoutInfo.total} data-currency="GBP" data-payment-method="paypal" onClick={() => { fetch('/api/checkout/dynamic', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(checkoutRequest(checkoutInfo, 'paypal')) }).then(r => r.json()).then(d => { if (d.url) window.location.href = d.url; }); }} style={{ width: '100%', height: '3rem', background: 'rgba(255,230,0,0.05)', border: '1px solid rgba(255,230,0,0.2)', color: '#FFE600', fontWeight: 'bold', fontSize: '0.875rem', borderRadius: '0.75rem', cursor: 'pointer' }}>
                Pay with PayPal
              </button>
            </div>
          </div>
        </div>
      )}

    </section>
  );
};

export default TravelPacks;
